import { createContext, useCallback, useContext, useEffect, useRef, type ReactNode } from 'react'
import { useDiRef } from '@/common/react/di-hooks'
import type { TutorialService } from './TutorialService'
import type { TutorialStepId, TutorialTargetId } from './tutorial-steps'

export const TutorialContext = createContext<TutorialService | null>(null)

export function TutorialProvider({ children }: { children: ReactNode }) {
  const service = useDiRef<TutorialService>('TutorialService')
  return <TutorialContext.Provider value={service}>{children}</TutorialContext.Provider>
}

function useTutorialService(): TutorialService {
  const service = useContext(TutorialContext)
  if (!service) throw new Error('useTutorial used outside TutorialProvider')
  return service
}

export function useTutorial() {
  const service = useTutorialService()
  return {
    advanceIfStep: useCallback((id: TutorialStepId) => service.advanceIfStep(id), [service]),
    skip: useCallback(() => service.skip(), [service]),
  }
}

export function useTutorialTarget(targetId: TutorialTargetId) {
  const service = useTutorialService()
  const elRef = useRef<HTMLElement | null>(null)

  const setRef = useCallback(
    (el: HTMLElement | null) => {
      if (elRef.current === el) return
      if (elRef.current) service.unregisterTarget(targetId, elRef.current)
      elRef.current = el
      if (el) service.registerTarget(targetId, el)
    },
    [service, targetId],
  )

  useEffect(() => {
    return () => {
      if (elRef.current) service.unregisterTarget(targetId, elRef.current)
    }
  }, [service, targetId])

  return setRef
}
